import type { HireOrganization, PeopleWorker } from './model';

export type PeopleFilters = {
  query: string;
  status: string;
  organization: string;
  organizationScope: string;
  location: string;
  company: string;
  workerType: string;
  issue: string;
};
export const emptyPeopleFilters: PeopleFilters = { query: '', status: '', organization: '', organizationScope: 'descendants', location: '', company: '', workerType: '', issue: '' };
export type PeopleSavedView = { id: string; name: string; filters: PeopleFilters };
export type PeopleViewPreferencesPort = {
  read(): PeopleSavedView[];
  write(views: PeopleSavedView[]): void;
};
export type PeopleIssue = { code: string; label: string; description: string };
export const peopleIssueOptions = [
  { value: 'any', label: '확인 필요 전체' },
  { value: 'organization', label: '소속 미설정' },
  { value: 'workerNumber', label: '사번 미설정' },
  { value: 'jobType', label: '고용 유형 미설정' },
  { value: 'email', label: '업무 이메일 미등록' },
  { value: 'duplicateEmail', label: '이메일 중복 의심' },
  { value: 'duplicateNumber', label: '사번 중복 의심' },
];
const missing = (value?: string) => !value?.trim() || ['—', '-', '미배치', '사번 미등록', '유형 미등록'].includes(value.trim());
const active = (worker: PeopleWorker) => !['퇴직', '종료', '등록 중', '입사 예정'].includes(worker.status);
const companyKey = (worker: PeopleWorker) => worker.companyId || worker.company || '';
const plainEmail = (value: string) => value.includes('@') && !value.includes('*') && !value.includes('•');

// Only the currently authorized roster is compared. Multiple employments for
// the same Person are not treated as duplicate people.
export function collectPeopleIssues(workers: readonly PeopleWorker[], canReadWorkContact: boolean): Map<string, PeopleIssue[]> {
  const emails = new Map<string, Set<string>>();
  const numbers = new Map<string, Set<string>>();
  for (const worker of workers.filter(active)) {
    if (canReadWorkContact && worker.personId && plainEmail(worker.email)) {
      const key = worker.email.trim().toLowerCase();
      const group = emails.get(key) ?? new Set<string>();
      group.add(worker.personId); emails.set(key, group);
    }
    if (!missing(worker.workerNumber) && companyKey(worker)) {
      const key = `${companyKey(worker)}\u0000${worker.workerNumber.trim().toLowerCase()}`;
      const group = numbers.get(key) ?? new Set<string>();
      group.add(worker.id); numbers.set(key, group);
    }
  }
  return new Map(workers.map((worker) => {
    const issues: PeopleIssue[] = [];
    if (active(worker)) {
      if (missing(worker.organization)) issues.push({ code: 'organization', label: '소속 미설정', description: '현재 소속을 확인하고 인사 발령으로 지정해 주세요.' });
      if (missing(worker.workerNumber)) issues.push({ code: 'workerNumber', label: '사번 미설정', description: '입사 요청의 사번과 사번 발급 정책을 확인해 주세요.' });
      if (missing(worker.jobType)) issues.push({ code: 'jobType', label: '고용 유형 미설정', description: '고용 유형을 확인하고 인사 발령으로 지정해 주세요.' });
      if (canReadWorkContact && missing(worker.email)) issues.push({ code: 'email', label: '업무 이메일 미등록', description: '구성원 상세의 연락처에서 업무 이메일을 등록해 주세요.' });
      if (canReadWorkContact && plainEmail(worker.email) && (emails.get(worker.email.trim().toLowerCase())?.size ?? 0) > 1) issues.push({ code: 'duplicateEmail', label: '이메일 중복 의심', description: '서로 다른 구성원이 같은 업무 이메일을 사용합니다. 연락처를 확인해 주세요.' });
      if ((numbers.get(`${companyKey(worker)}\u0000${worker.workerNumber.trim().toLowerCase()}`)?.size ?? 0) > 1) issues.push({ code: 'duplicateNumber', label: '사번 중복 의심', description: '같은 회사에서 같은 사번을 사용하는 고용관계가 있습니다. 입사 이력을 확인해 주세요.' });
    }
    return [worker.id, issues];
  }));
}

export function peopleOrganizationOptions(workers: readonly PeopleWorker[], organizations: readonly HireOrganization[]) {
  const byId = new Map(organizations.map(organization => [organization.id, organization]));
  for (const worker of workers) for (const membership of [worker, ...worker.additionalJobs]) {
    if (membership.organizationId && !byId.has(membership.organizationId)) byId.set(membership.organizationId, { id: membership.organizationId, descriptor: membership.organization });
  }
  return [...byId.values()].map(organization => {
    const path: string[] = []; const seen = new Set<string>();
    let current: HireOrganization | undefined = organization;
    while (current && !seen.has(current.id)) { seen.add(current.id); path.unshift(current.descriptor || '이름 없는 조직'); current = current.parent ? byId.get(current.parent.id) : undefined; }
    return { value: organization.id, label: path.join(' › ') };
  }).sort((left, right) => left.label.localeCompare(right.label, 'ko-KR'));
}
export function peopleOrganizationScope(organizationId: string, organizations: readonly HireOrganization[], includeDescendants: boolean) {
  const scope = new Set([organizationId]);
  if (includeDescendants) {
    let changed = true;
    while (changed) { changed = false; for (const organization of organizations) if (organization.parent && scope.has(organization.parent.id) && !scope.has(organization.id)) { scope.add(organization.id); changed = true; } }
  }
  return scope;
}
export function filterPeople(workers: readonly PeopleWorker[], filters: PeopleFilters, issues: Map<string, PeopleIssue[]>, organizations: readonly HireOrganization[] = []): PeopleWorker[] {
  const query = filters.query.trim().toLocaleLowerCase();
  // 예전 브라우저에 이름으로 저장한 필터는 이름이 유일할 때만 ID로 복구합니다.
  const legacyMatches = organizations.filter(item => item.descriptor === filters.organization);
  const organizationId = organizations.some(item => item.id === filters.organization) ? filters.organization : legacyMatches.length === 1 ? legacyMatches[0].id : filters.organization;
  const scope = peopleOrganizationScope(organizationId, organizations, filters.organizationScope !== 'direct');
  return workers.filter((worker) =>
    `${worker.name} ${worker.workerNumber} ${worker.organization} ${worker.additionalJobs.map(job => job.organization).join(' ')}`.toLocaleLowerCase().includes(query)
    && (!filters.status || worker.status === filters.status)
    && (!filters.organization || [worker, ...worker.additionalJobs].some(membership => membership.organizationId && scope.has(membership.organizationId)))
    && (!filters.location || worker.location === filters.location)
    && (!filters.company || companyKey(worker) === filters.company)
    && (!filters.workerType || (worker.jobTypeId || worker.jobType || '') === filters.workerType)
    && (!filters.issue || (issues.get(worker.id) ?? []).some((issue) => filters.issue === 'any' || filters.issue === issue.code)));
}

export function parsePeopleSavedViews(value: unknown): PeopleSavedView[] {
  if (!Array.isArray(value)) return [];
  return value.slice(0, 20).flatMap((item: unknown) => {
    if (!item || typeof item !== 'object' || !('id' in item) || typeof item.id !== 'string' || !('name' in item) || typeof item.name !== 'string' || !item.name.trim() || !('filters' in item) || !item.filters || typeof item.filters !== 'object') return [];
    const filters = { ...emptyPeopleFilters };
    for (const key of Object.keys(filters) as (keyof PeopleFilters)[]) {
      const input = (item.filters as Record<string, unknown>)[key];
      if (typeof input === 'string') filters[key] = input.slice(0, 300);
    }
    return [{ id: item.id.slice(0, 100), name: item.name.trim().slice(0, 40), filters }];
  });
}
